import React, { useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Users, FileCheck, Briefcase, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import { useCMSContent } from '../hooks/useCMSContent';
import SEO from '../components/SEO';

const AboutPage = () => {
  const { t, language } = useLanguage();
  const { blocks } = useCMSContent('about');

  // Helper to get CMS content with language support, falling back to translations
  const getCMSText = useCallback((blockKey, field, translationKey) => {
    const block = blocks[blockKey];
    if (block && Object.keys(block).length > 0) {
      const langField = language === 'es' ? `${field}_es` : field;
      return block[langField] || block[field] || t(translationKey);
    }
    return t(translationKey);
  }, [blocks, language, t]);
  
  const practiceFocus = useMemo(() => [
    t('services.trademarks.title'),
    t('services.businessFormation.title'),
    t('services.contracts.title'),
    t('services.governmentContracts.title'),
    t('services.personalInjury.title'),
  ], [t]);

  const credentials = useMemo(() => [
    { icon: Award, text: t('about.credential1') },
    { icon: Briefcase, text: t('about.credential2') },
    { icon: Users, text: t('about.credential3') },
    { icon: FileCheck, text: t('about.credential4') },
    { icon: Scale, text: t('about.credential5') },
  ], [t]);

  useEffect(() => {
    publicApi.trackEvent({ event_type: 'page_view', page: 'about' }).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <SEO 
        title="About Attorney Jennifer N. Brantley"
        description="Meet Jennifer N. Brantley, an experienced attorney specializing in trademarks, business formation, and contracts. Licensed in Georgia and Texas with 13+ years of experience."
      />
      {/* Hero Section */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
                {getCMSText('hero', 'badge', 'about.sectionLabel')}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
                {getCMSText('hero', 'headline', 'about.headline')}
              </h1>
              <p className="text-xl md:text-2xl font-serif text-[#1F2A44] mb-8">
                {getCMSText('hero', 'tagline', 'about.tagline')}
              </p>
              <div className="prose prose-lg text-[#555555] leading-relaxed space-y-4">
                <p>{getCMSText('bio', 'paragraph1', 'about.bio1')}</p>
                <p>{getCMSText('bio', 'paragraph2', 'about.bio2')}</p>
                <p>{getCMSText('bio', 'paragraph3', 'about.bio3')}</p>
              </div>
            </div>
            <div className="space-y-6">
              {/* Main portrait with decorative frame */}
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-full h-full border border-[#C2A46D]/30"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_legal-consultations/artifacts/m6k2h8dj_photo%20dec%2030%202023%2C%2012%2045%2012%20pm%20%282%29.PNG"
                  alt="Jennifer N. Brantley - Professional Portrait"
                  className="w-full h-auto object-cover shadow-xl"
                  data-testid="about-portrait"
                />
              </div>
              {/* Lifestyle photos collage */}
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_legal-consultations/artifacts/m5kicpx7_6627179B-FAF1-4CD5-8BFA-69DEEC91FB7F.JPEG"
                  alt="Jennifer Brantley at work"
                  className="w-full h-48 object-cover object-center border border-[#E8E4DC]"
                  data-testid="about-lifestyle-1"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_strategic-counsel-6/artifacts/3x9uguqi_photo%20dec%2030%202023_%2012%2045%2013%20pm%20%281%29.JPEG"
                  alt="Jennifer Brantley with client"
                  className="w-full h-48 object-cover border border-[#E8E4DC]"
                  data-testid="about-lifestyle-2"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Legal Architect Story */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
              {getCMSText('brand_story', 'section_label', 'about.brandStoryLabel')}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-8">
              {getCMSText('brand_story', 'headline', 'about.brandStoryTitle')}
            </h2>
            <div className="text-[#555555] leading-relaxed space-y-4 text-left">
              <p>{getCMSText('brand_story', 'paragraph1', 'about.brandStory1')}</p>
              <p>{getCMSText('brand_story', 'paragraph2', 'about.brandStory2')}</p>
              <p>{getCMSText('brand_story', 'paragraph3', 'about.brandStory3')}</p>
              <p>{getCMSText('brand_story', 'paragraph4', 'about.brandStory4')}</p>
              <p>{getCMSText('brand_story', 'paragraph5', 'about.brandStory5')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="bg-[#F5F3EF] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {getCMSText('credentials', 'section_label', 'about.credentialsLabel')}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222]">
              {getCMSText('credentials', 'headline', 'about.credentialsTitle')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {credentials.map((credential, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-white p-6 border border-[#E8E4DC]"
                data-testid={`credential-${index}`}
              >
                <credential.icon className="w-8 h-8 text-[#1F2A44] flex-shrink-0" />
                <span className="text-[#222222] font-medium">{credential.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Focus Section */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
                {t('about.practiceFocusLabel')}
              </p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-8">
                {t('about.practiceFocusTitle')}
              </h2>
              <ul className="space-y-4">
                {practiceFocus.map((item, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`practice-focus-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#1F2A44] mt-0.5 flex-shrink-0" />
                    <span className="text-[#555555]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#1F2A44] p-10 md:p-12 text-white">
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
                {t('about.philosophyLabel')}
              </p>
              <h3 className="text-2xl md:text-3xl font-serif mb-6">
                {t('about.philosophyTitle')}
              </h3>
              <p className="text-white/80 leading-relaxed mb-6">
                {t('about.philosophyDesc1')}
              </p>
              <p className="text-white/80 leading-relaxed">
                {t('about.philosophyDesc2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#FDFCFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
            {t('about.ctaTitle')}
          </h2>
          <p className="text-lg text-[#555555] mb-10 max-w-2xl mx-auto">
            {t('about.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" data-testid="about-cta-book">
              <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                {t('about.ctaBook')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/services" data-testid="about-cta-services">
              <Button variant="outline" className="border-[#1F2A44] text-[#1F2A44] hover:bg-[#1F2A44] hover:text-white px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                {t('about.ctaServices')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
